import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import { useAuth } from '../context/AuthContext';
import './ProductDetails.css';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  categoryId?: string;
  stock: number;
  imageUrl?: string;
}

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [membershipDiscountPercent, setMembershipDiscountPercent] = useState(0);
  const { showSuccess, showError } = useSnackbar();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      setLoading(true);
      setError('');
      try {
        const response = await api.get(`/products/${id}`);
        setProduct(response.data?.product || null);
        setMembershipDiscountPercent(response.data?.membershipDiscountPercent || 0);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Unable to load product');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const addToCart = async () => {
    if (!product) return;
    if (!user) {
      showError('Please login or register to add items to cart');
      navigate('/login', { state: { returnTo: `/products/${product.id}` } });
      return;
    }

    try {
      await api.post('/cart/add', {
        productId: product.id,
        quantity: parseInt(String(quantity)),
      });
      showSuccess('Item added to cart!');
      setQuantity(1);
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Please login to add items to cart');
        navigate('/login', { state: { returnTo: `/products/${product.id}` } });
      } else {
        showError(err.response?.data?.error || 'Failed to add item to cart');
      }
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="product-details-skeleton">
          <div className="product-details-skeleton-media" />
          <div className="product-details-skeleton-body">
            <div className="product-details-skeleton-line title" />
            <div className="product-details-skeleton-line meta" />
            <div className="product-details-skeleton-line meta short" />
            <div className="product-details-skeleton-line price" />
            <div className="product-details-skeleton-line meta" />
          </div>
        </div>
      </div>
    );
  }

  if (!product || error) {
    return (
      <div className="container">
        <div className="product-details-error">
          <div className="product-details-error-icon">🧺</div>
          <h2>Product not found</h2>
          <p>{error || 'We could not find this product.'}</p>
          <Link to="/products" className="btn btn-secondary">
            Back to products
          </Link>
        </div>
      </div>
    );
  }

  const discountedPrice = product.price * (1 - membershipDiscountPercent / 100);

  return (
    <div className="container product-details-page">
      <div className="product-details-header">
        <Link to="/products" className="product-details-back">
          ← Back to products
        </Link>
      </div>

      <div className="product-details-card">
        <div className="product-details-media">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} />
          ) : (
            <div className="product-details-placeholder">No image</div>
          )}
        </div>

        <div className="product-details-content">
          <div className="product-category">{product.category}</div>
          <h1>{product.name}</h1>
          <div
            className="product-description product-description-html"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(product.description || ''),
            }}
          />

          <div className="product-pricing">
            <div className="price-row">
              <span className="product-price">${discountedPrice.toFixed(2)}</span>
              {membershipDiscountPercent > 0 && (
                <span className="discount-badge">{membershipDiscountPercent}% member discount</span>
              )}
            </div>
            {membershipDiscountPercent > 0 && (
              <div className="base-price">Base price ${product.price.toFixed(2)}</div>
            )}
          </div>

          <div className="product-details-meta">
            <span>Stock: {product.stock}</span>
          </div>

          <div className="product-actions">
            <input
              type="number"
              min="1"
              max={product.stock}
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className="quantity-input"
              disabled={product.stock === 0}
            />
            <button className="btn btn-primary" onClick={addToCart} disabled={product.stock === 0}>
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
